import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as forumApi from '../api/forum';
import { usePagedList } from '../lib/usePagedList';
import { useAuth } from '../lib/auth';
import { useCategoryModerator } from '../lib/forumModeration';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  Field,
  TextareaField,
  PagedListFooter,
  RelativeTime,
  UserChip,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const THREADS_PAGE_SIZE = 25;

export default function ForumCategory() {
  const { categoryId } = useParams();
  const { user, isStaff } = useAuth();
  // FEA-30: widen moderation from staff-only to staff OR a member designated
  // a moderator of this category (docs/FRONTEND-PLAN.md §4.3).
  const { isModerator } = useCategoryModerator(categoryId, { userId: user?.userId, isStaff });
  const confirm = useConfirm();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  // Compact moderation actions (FEA-19): threadId of the row currently
  // running a lock/pin/delete call, or null.
  const [modBusyId, setModBusyId] = useState(null);

  const list = usePagedList(
    cursor =>
      forumApi
        .listThreads(categoryId, { limit: THREADS_PAGE_SIZE, lastEvaluatedKey: cursor })
        .then(d => ({ items: d.threads || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [categoryId]
  );

  async function togglePin(thread) {
    setModBusyId(thread.threadId);
    try {
      const { thread: updated } = thread.isPinned
        ? await forumApi.unpinThread(thread.threadId)
        : await forumApi.pinThread(thread.threadId);
      list.mutateItem(thread.threadId, { isPinned: updated.isPinned }, 'threadId');
      toast.success(updated.isPinned ? 'Thread pinned.' : 'Thread unpinned.');
    } catch (err) {
      toast.error(err);
    } finally {
      setModBusyId(null);
    }
  }

  async function toggleLock(thread) {
    setModBusyId(thread.threadId);
    try {
      const { thread: updated } = thread.isLocked
        ? await forumApi.unlockThread(thread.threadId)
        : await forumApi.lockThread(thread.threadId);
      list.mutateItem(thread.threadId, { isLocked: updated.isLocked }, 'threadId');
      toast.success(updated.isLocked ? 'Thread locked.' : 'Thread unlocked.');
    } catch (err) {
      toast.error(err);
    } finally {
      setModBusyId(null);
    }
  }

  async function deleteThread(thread) {
    const ok = await confirm({
      title: 'Delete thread?',
      message: `Delete "${thread.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setModBusyId(thread.threadId);
    try {
      await forumApi.deleteThread(thread.threadId);
      list.removeItem(thread.threadId, 'threadId');
      toast.success('Thread deleted.');
    } catch (err) {
      toast.error(err);
      setModBusyId(null);
    }
  }

  async function onCreate(event) {
    event.preventDefault();
    setBusy(true);
    setCreateError(null);
    try {
      await forumApi.createThread(categoryId, { title, content: body });
      setTitle('');
      setBody('');
      toast.success('Thread posted.');
      list.reload();
    } catch (err) {
      setCreateError(err);
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p>
        <Link to="/forum">← All categories</Link>
      </p>
      <h1 className={ui.h1}>Threads</h1>

      <section className={cn(ui.card, 'mb-4')}>
        <h3 className={ui.h3}>Start a thread</h3>
        <ErrorAlert error={createError} />
        <form onSubmit={onCreate}>
          <Field label="Title" required value={title} onChange={e => setTitle(e.target.value)} />
          <TextareaField
            label="Message"
            rows={4}
            required
            value={body}
            onChange={e => setBody(e.target.value)}
          />
          <button className={ui.btnPrimary} type="submit" disabled={busy}>
            {busy ? 'Posting…' : 'Post thread'}
          </button>
        </form>
      </section>

      <ErrorAlert error={list.error} />

      {list.loading ? (
        <Spinner />
      ) : list.items.length === 0 ? (
        !list.error && (
          <EmptyState>No threads in this category yet — start the first one.</EmptyState>
        )
      ) : (
        <div className={cn(ui.card, ui.tableWrap)}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Thread</th>
                <th>Author</th>
                <th>Replies</th>
                <th>Last post</th>
                {isModerator && <th>Moderate</th>}
              </tr>
            </thead>
            <tbody>
              {list.items.map(t => (
                <tr key={t.threadId}>
                  <td>
                    <Link to={`/forum/threads/${t.threadId}`}>{t.title}</Link>
                    {t.isPinned && <span className={ui.pillInfo}> Pinned</span>}
                    {t.isLocked && <span className={ui.pillNeutral}> Locked</span>}
                  </td>
                  <td>
                    <UserChip userId={t.createdBy} name={t.createdByName} />
                  </td>
                  <td>{t.postCount ?? '—'}</td>
                  <td>
                    <RelativeTime iso={t.lastPostAt || t.createdAt} />
                  </td>
                  {isModerator && (
                    <td className={ui.row}>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        disabled={modBusyId === t.threadId}
                        onClick={() => togglePin(t)}
                      >
                        {t.isPinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button
                        type="button"
                        className={ui.btnSecondary}
                        disabled={modBusyId === t.threadId}
                        onClick={() => toggleLock(t)}
                      >
                        {t.isLocked ? 'Unlock' : 'Lock'}
                      </button>
                      <button
                        type="button"
                        className={ui.btnDanger}
                        disabled={modBusyId === t.threadId}
                        onClick={() => deleteThread(t)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PagedListFooter
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        endLabel={list.items.length ? 'No more threads.' : null}
      />
    </>
  );
}
